import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Edit2, Trash2, Search, Tag, X, CheckCircle2, CircleSlash, RefreshCw } from 'lucide-react';
import http from '../api/http';
import BlueHeader from '../components/BlueHeader';

/* ───────────── Styles partagés (identiques à la vue Filiales) ───────────── */

const inputCls =
  'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm ' +
  'placeholder:text-slate-400 transition focus:border-slate-400 focus:outline-none focus:ring-4 focus:ring-slate-900/5 ' +
  'dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:placeholder:text-slate-500 dark:focus:border-slate-500 dark:focus:ring-white/10';

const btnPrimary =
  'inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-sm ' +
  'transition hover:bg-slate-800 focus:outline-none focus:ring-4 focus:ring-slate-900/20 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200';

const btnGhost =
  'inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 ' +
  'transition hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-slate-900/5 ' +
  'dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700';

const iconBtn =
  'rounded-lg p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-300 ' +
  'dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-white';

const EMPTY_FORM = { code: '', libelle: '', description: '', actif: true };

const FILTERS = [
  { id: 'all', label: 'Toutes' },
  { id: 'actif', label: 'Actives' },
  { id: 'inactif', label: 'Inactives' },
];

/* ───────────── Petits composants ───────────── */

const Field = ({ label, children }) => (
  <div>
    <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">{label}</label>
    {children}
  </div>
);

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
      <Icon className="h-5 w-5" />
    </div>
    <div>
      <p className="text-2xl font-semibold leading-none text-slate-900 dark:text-white">{value}</p>
      <p className="mt-1.5 text-sm text-slate-500 dark:text-slate-400">{label}</p>
    </div>
  </div>
);

const Switch = ({ checked, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition focus:outline-none focus:ring-4 focus:ring-slate-900/10 ${checked ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`}
  >
    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
  </button>
);

/* ───────────── Vue principale ───────────── */

const Marques = () => {
  const [marques, setMarques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMarque, setEditingMarque] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchMarques = async () => {
    try {
      setLoading(true);
      const response = await http.get('/api/marques');
      setMarques(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur récupération marques:', error);
      setMarques([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMarques(); }, []);

  const stats = useMemo(() => {
    const actives = marques.filter((m) => m.actif).length;
    return { total: marques.length, actives, inactives: marques.length - actives };
  }, [marques]);

  const filteredMarques = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return marques.filter((m) => {
      const matchSearch = !q || (m.code || '').toLowerCase().includes(q) || (m.libelle || '').toLowerCase().includes(q);
      const matchStatus = statusFilter === 'all' || (statusFilter === 'actif' ? !!m.actif : !m.actif);
      return matchSearch && matchStatus;
    });
  }, [marques, searchTerm, statusFilter]);

  const closeModal = () => {
    setShowModal(false);
    setEditingMarque(null);
    setFormData(EMPTY_FORM);
  };

  const handleAdd = () => {
    setEditingMarque(null);
    setFormData(EMPTY_FORM);
    setShowModal(true);
  };

  const handleEdit = (marque) => {
    setEditingMarque(marque);
    setFormData({
      code: marque.code,
      libelle: marque.libelle,
      description: marque.description || '',
      actif: marque.actif,
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editingMarque) await http.put(`/api/marques/${editingMarque.id}`, formData);
      else await http.post('/api/marques', formData);
      closeModal();
      fetchMarques();
    } catch (error) {
      console.error('Erreur sauvegarde marque:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette marque ?')) return;
    try {
      await http.delete(`/api/marques/${id}`);
      fetchMarques();
    } catch (error) {
      console.error('Erreur suppression marque:', error);
      alert('Erreur lors de la suppression');
    }
  };

  return (
    <div className="min-h-full bg-slate-50 dark:bg-slate-950">
      <BlueHeader
        title="Marques"
        subtitle="Gestion des marques"
        icon={Tag}
        showBack={false}
        actions={[
          { label: 'Créer', icon: Plus, onClick: handleAdd, primary: true },
          { label: 'Actualiser', icon: RefreshCw, onClick: fetchMarques, primary: false },
        ]}
      />

      <div className="space-y-6 p-6">
        {/* Indicateurs */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard icon={Tag} label="Marques" value={stats.total} />
          <StatCard icon={CheckCircle2} label="Actives" value={stats.actives} />
          <StatCard icon={CircleSlash} label="Inactives" value={stats.inactives} />
        </div>

        {/* Liste */}
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="flex flex-col gap-3 border-b border-slate-200 px-5 py-4 dark:border-slate-800 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative w-full sm:w-72">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="search"
                  placeholder="Rechercher par code ou libellé"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`${inputCls} pl-9`}
                />
              </div>
              <div className="inline-flex rounded-lg bg-slate-100 p-1 dark:bg-slate-800">
                {FILTERS.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setStatusFilter(f.id)}
                    className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
                      statusFilter === f.id
                        ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-white'
                        : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="space-y-3 p-5">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-14 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-800" />
              ))}
            </div>
          ) : filteredMarques.length === 0 ? (
            <div className="flex flex-col items-center px-4 py-16 text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-slate-400 dark:bg-slate-800">
                <Tag className="h-6 w-6" />
              </div>
              <p className="font-medium text-slate-900 dark:text-white">
                {marques.length ? 'Aucun résultat' : 'Aucune marque pour le moment'}
              </p>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {marques.length ? 'Modifiez votre recherche ou le filtre.' : 'Créez votre première marque pour commencer.'}
              </p>
              {!marques.length && (
                <button type="button" onClick={handleAdd} className={`${btnPrimary} mt-5`}>
                  <Plus className="h-4 w-4" /> Créer une marque
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/70 text-left text-xs font-semibold text-slate-500 dark:border-slate-800 dark:bg-slate-800/40 dark:text-slate-400">
                    <th className="px-5 py-3">Marque</th>
                    <th className="px-5 py-3">Description</th>
                    <th className="px-5 py-3">Statut</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {filteredMarques.map((marque) => (
                    <tr key={marque.id} className="transition hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-sm font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                            {(marque.libelle || marque.code || '?').charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">{marque.libelle}</p>
                            <p className="mt-0.5 text-xs">
                              <span className="rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300">{marque.code}</span>
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="max-w-md px-5 py-3.5 text-sm text-slate-600 dark:text-slate-300">
                        <p className="line-clamp-2">{marque.description || '-'}</p>
                      </td>
                      <td className="px-5 py-3.5">
                        {marque.actif ? (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Actif
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                            <span className="h-1.5 w-1.5 rounded-full bg-slate-400" /> Inactif
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex justify-end gap-1">
                          <button type="button" onClick={() => handleEdit(marque)} className={iconBtn} title="Modifier">
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(marque.id)}
                            className="rounded-lg p-2 text-slate-500 transition hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-200 dark:text-slate-400 dark:hover:bg-red-500/10 dark:hover:text-red-400"
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal création / modification */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-4 dark:border-slate-800">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  {editingMarque ? 'Modifier la marque' : 'Créer une marque'}
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {editingMarque ? editingMarque.libelle : 'Renseignez les informations de la marque'}
                </p>
              </div>
              <button type="button" onClick={closeModal} className={iconBtn} aria-label="Fermer">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSave}>
              <div className="space-y-5 p-6">
                <Field label="Code">
                  <input
                    type="text"
                    className={inputCls}
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    required
                  />
                </Field>
                <Field label="Libellé">
                  <input
                    type="text"
                    className={inputCls}
                    value={formData.libelle}
                    onChange={(e) => setFormData({ ...formData, libelle: e.target.value })}
                    required
                  />
                </Field>
                <Field label="Description">
                  <textarea
                    className={inputCls}
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </Field>
                <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 dark:border-slate-800 dark:bg-slate-800/40">
                  <div>
                    <p className="text-sm font-medium text-slate-900 dark:text-white">Marque active</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {formData.actif ? 'Visible et utilisable' : 'Désactivée'}
                    </p>
                  </div>
                  <Switch checked={!!formData.actif} label="Marque active" onChange={(v) => setFormData({ ...formData, actif: v })} />
                </div>
              </div>

              <div className="flex justify-end gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-900/60">
                <button type="button" onClick={closeModal} className={btnGhost}>Annuler</button>
                <button type="submit" className={btnPrimary}>{editingMarque ? 'Enregistrer' : 'Créer'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Marques;